import { useMutation } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { useLocation, useNavigate } from "react-router-dom";

import {
  challengeTwoFactorAuth,
  challengeTwoFactorRecoveryCode,
  confirmTwoFactorAuth,
  enableTwoFactorAuth,
  login,
  logout,
  signUp,
} from "@/api/auth";
import type { ErrorResponse } from "@/api/axios";
import { ROUTES } from "@/router";
import { useMultiStepFormStore, useUserStore } from "@/stores";

export interface LocationState {
  from?: {
    pathname?: string;
    search?: string;
  };
}

export const useAuthentication = () => {
  const useLogin = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { clearUser, setToken, setTokenExpiresAt } = useUserStore();
    const { clearMultiStepFormData } = useMultiStepFormStore();

    return useMutation({
      mutationFn: login,
      onError: (error: ErrorResponse) => {
        if (error.response?.status === 401) {
          error.message = "Invalid email or password";
        } else {
          error.message =
            "An unexpected error occurred, please try again later.";
        }
      },
      onSuccess: ({ data }) => {
        clearUser();
        clearMultiStepFormData();
        setToken(data.accessToken);
        setTokenExpiresAt(data.expiresIn);
        const state = location.state as LocationState;
        const { from } = state || {};

        if (
          data?.message ===
          "Valid credentials. Please complete 2FA to continue."
        ) {
          void navigate(ROUTES.twoFactorAuthChallenge, {
            state: { from },
          });
        } else {
          void navigate(ROUTES.twoFactorAuthSetup, {
            state: { from },
          });
        }
      },
    });
  };

  const useLogout = () => {
    const navigate = useNavigate();
    const { clearUser } = useUserStore();
    const { clearMultiStepFormData } = useMultiStepFormStore();

    return useMutation({
      mutationFn: logout,
      onSuccess: () => {
        clearUser();
        clearMultiStepFormData();
        void navigate(ROUTES.login);
      },
    });
  };

  const useSignUp = () => {
    const navigate = useNavigate();

    return useMutation({
      mutationFn: signUp,
      onSuccess: () => {
        toast.success("Successful sign up");
        void navigate(ROUTES.login);
      },
    });
  };

  const useEnableTwoFactor = () => {
    return useMutation({
      mutationFn: enableTwoFactorAuth,
    });
  };

  const useConfirmTwoFactor = () => {
    const setToken = useUserStore((state) => state.setToken);

    return useMutation({
      mutationFn: confirmTwoFactorAuth,
      onSuccess: ({ data }) => {
        setToken(data.accessToken);
      },
    });
  };

  const useChallengeTwoFactor = () => {
    const { setUser } = useUserStore();
    const setToken = useUserStore((state) => state.setToken);
    const setTokenExpiresAt = useUserStore((state) => state.setTokenExpiresAt);
    const setIsTwoFaAuthenticated = useUserStore(
      (state) => state.setIsTwoFaAuthenticated,
    );
    return useMutation({
      mutationFn: challengeTwoFactorAuth,
      onSuccess: ({ data }) => {
        setUser({
          name: data.userName,
          role: data.userRole,
        });
        setToken(data.accessToken);
        setTokenExpiresAt(data.expiresIn);
        setIsTwoFaAuthenticated(true);
      },
    });
  };

  const useChallengeRecoveryCode = () => {
    const { setUser } = useUserStore();
    const setToken = useUserStore((state) => state.setToken);
    const setTokenExpiresAt = useUserStore((state) => state.setTokenExpiresAt);
    const setIsTwoFaAuthenticated = useUserStore(
      (state) => state.setIsTwoFaAuthenticated,
    );
    return useMutation({
      mutationFn: challengeTwoFactorRecoveryCode,
      onSuccess: ({ data }) => {
        setUser({
          name: data.userName,
          role: data.userRole,
        });
        setToken(data.accessToken);
        setTokenExpiresAt(data.expiresIn);
        setIsTwoFaAuthenticated(true);
      },
    });
  };

  return {
    useLogout,
    useLogin,
    useSignUp,
    useEnableTwoFactor,
    useConfirmTwoFactor,
    useChallengeTwoFactor,
    useChallengeRecoveryCode,
  };
};
